import { Formik, Form } from "formik";
import { MedicalTravelModel } from "../types/FormTypes";
import { FOR_ME, FOR_PATIENT } from "./MedicalTravel";
import { useAtom } from "jotai";
import { formDataAtom } from "../App";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { validateSwedishSsn } from "swedish-ssn";
import { useRef } from "react";
import useFocusOnLoad from "../hooks/useFocusOnLoad";
import { URLS } from "src/config/urls";

import {
  Box,
  Progressbar,
  ErrorList,
  handleSubmitClick,
  Button,
  InputText,
  Link,
  InputTextNumber,
  Heading,
} from "@ogt/ui";

export interface ContactDetailsFormValues extends MedicalTravelModel {
  patientemailrepeat: string;
  caregiveremailrepeat: string;
}

export default function ContactDetails() {
  const h2Ref = useFocusOnLoad<HTMLHeadingElement>();
  const [formData, setFormData] = useAtom(formDataAtom);
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const alaphaBetRegEx = /^[^a-ö]*$/;
  const progress = 12;

  return (
    <div>
      <Formik<ContactDetailsFormValues>
        onSubmit={(values, { setSubmitting, setErrors }) => {
          setFormData({
            ...formData,
            ...values,
            ...{
              patientemailrepeat: undefined,
              caregiveremailrepeat: undefined,
            },
          });

          navigate("/utbetalning");
        }}
        initialValues={{
          ...(formData as ContactDetailsFormValues),
          ...(formData.compensationfor === FOR_PATIENT
            ? {
              patientemail: "",
              patientemailrepeat: "",
              patientphone: "",
              caregiveremailrepeat: formData.caregiveremail,
            }
            : {
              patientemailrepeat: formData.patientemail,
              caregiverssn: "",
              caregiverfirstname: "",
              caregiverlastname: "",
              caregiveremail: "",
              caregiveremailrepeat: "",
              caregiverphone: "",
            }),
        }}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={Yup.object().shape({
          patientssn: Yup.string()
            .required("Vänligen fyll i patientens personnummer")
            .matches(
              /^\d{6}-\d{4}$/,
              "Vänligen fyll i ett giltigt personnummer för patienten med endast 10 siffror "
            )
            .test(
              "valid-swedish-ssn",
              "Vänligen fyll i ett giltigt personnummer",
              (value) => {
                return validateSwedishSsn(value);
              }
            ),

          patientfirstname: Yup.string().required(
            "Vänligen fyll i patientens förnamn"
          ),

          patientlastname: Yup.string().required(
            "Vänligen fyll i patientens efternamn"
          ),

          patientemail: Yup.string().when("compensationfor", {
            is: FOR_PATIENT,
            then: (schema) => schema.notRequired(),
            otherwise: (schema) =>
              schema
                .email("Kontrollera att mejladressen är rätt")
                .required("Vänligen fyll i patientens mejladress"),
          }),

          patientemailrepeat: Yup.string().when("compensationfor", {
            is: FOR_PATIENT,
            then: (schema) => schema.notRequired(),
            otherwise: (schema) =>
              schema
                .email("Kontrollera att mejladressen är rätt")
                .required("Vänligen upprepa patientens mejladress")
                .oneOf(
                  [Yup.ref("patientemail")],
                  "Mejladress stämmer inte med förra inskrivningen"
                ),
          }),

          patientphone: Yup.string()
            .matches(
              alaphaBetRegEx,
              "Telefonnummer får inte innehålla bokstäver"
            )
            .when("compensationfor", {
              is: FOR_PATIENT,
              then: (schema) => schema,
            }),

          caregiverssn: Yup.string().when("compensationfor", {
            is: FOR_PATIENT,
            then: (schema) =>
              schema
                .required("Vänligen fyll i vårdnadshavarens personnummer")
                .matches(
                  /^\d{6}-\d{4}$/,
                  "Vänligen fyll i ett giltigt personnummer för vårdnadshavaren med endast 10 siffror"
                )
                .test(
                  "valid-swedish-ssn",
                  "Vänligen fyll i ett giltigt personnummer",
                  (value) => {
                    return validateSwedishSsn(value);
                  }
                )
                .test(
                  "not-same-as-patientssn",
                  "Vårdnadshavarens personnummer får inte vara samma som patientens personnummer",
                  function (value) {
                    return value !== this.parent.patientssn;
                  }
                ),

            otherwise: (schema) => schema.notRequired(),
          }),
          caregiverfirstname: Yup.string().when("compensationfor", {
            is: FOR_PATIENT,
            then: (schema) =>
              schema.required("Vänligen fyll i vårdnadshavarens förnamn"),
            otherwise: (schema) => schema.notRequired(),
          }),
          caregiverlastname: Yup.string().when("compensationfor", {
            is: FOR_PATIENT,
            then: (schema) =>
              schema.required("Vänligen fyll i vårdnadshavarens efternamn"),
            otherwise: (schema) => schema.notRequired(),
          }),
          caregiveremail: Yup.string().when("compensationfor", {
            is: FOR_PATIENT,
            then: (schema) =>
              schema
                .email("Kontrollera att mejladressen är rätt")
                .required("Vänligen fyll i vårdnadshavarens mejladress"),
            otherwise: (schema) => schema.notRequired(),
          }),
          caregiveremailrepeat: Yup.string().when("compensationfor", {
            is: FOR_PATIENT,
            then: (schema) =>
              schema
                .email("Vänligen ange en korrekt epostadress")
                .required("Vänligen upprepa vårdnadshavarens mejladress")
                .oneOf(
                  [Yup.ref("caregiveremail")],
                  "Mejladress stämmer inte med förra inskrivningen"
                ),
            otherwise: (schema) => schema.notRequired(),
          }),

          caregiverphone: Yup.string().when("compensationfor", {
            is: FOR_PATIENT,
            then: (schema) =>
              schema.matches(
                alaphaBetRegEx,
                "Telefonnummer får inte innehålla bokstäver"
              ),
          }),
        })}
      >
        {({ errors, values, handleChange }) => (
          <>
            <div className="flex justify-end items-center">
              <div className="flex justify-center h-15 md:h-auto mb-1"></div>

              <div className="w-40 w-full max-w-xs mb-4">
                <Progressbar percentage={progress} />
              </div>
            </div>
            <Form ref={formRef} noValidate>
              <Box>
                <Heading progress={progress} headingRef={h2Ref} focusable>
                  Ange kontaktuppgifter
                </Heading>

                <ErrorList errors={errors} />

                <Heading as="h3">Patient</Heading>

                <InputText
                  name="patientssn"
                  label="Patientens personnummer (Obligatorisk)"
                  moreinfo="ÅÅMMDD-XXXX (10 siffror)"
                  ssn
                />

                <InputText
                  name="patientfirstname"
                  label="Patientens förnamn (Obligatorisk)"
                  autocomplete={
                    formData.compensationfor === FOR_ME ? "given-name" : "off"
                  }
                />

                <InputText
                  name="patientlastname"
                  label="Patientens efternamn (Obligatorisk)"
                  autocomplete={
                    formData.compensationfor === FOR_ME ? "family-name" : "off"
                  }
                />

                {formData.compensationfor === FOR_PATIENT ? (
                  <>
                    <Heading as="h3" className="mt-3">
                      Vårdnadshavare{" "}
                    </Heading>

                    <InputText
                      name="caregiverssn"
                      label={
                        <>
                          Vårdnadshavarens personnummer <span>(Obligatorisk)</span>
                        </>
                      }
                      moreinfo="ÅÅMMDD-XXXX (10 siffror)"
                      ssn
                    />

                    <InputText
                      name="caregiverfirstname"
                      label={
                        <>
                          Vårdnadshavarens förnamn <span>(Obligatorisk)</span>
                        </>
                      }
                      autocomplete={"given-name"}
                    />

                    <InputText
                      name="caregiverlastname"
                      label={
                        <>
                          Vårdnadshavarens efternamn <span>(Obligatorisk)</span>
                        </>
                      }
                      autocomplete={"family-name"}
                    />

                    <Heading as="h3" className="mt-10">
                      Vårdnadshavarens kontaktuppgifter
                    </Heading>

                    <InputText
                      name="caregiveremail"
                      label={
                        <>
                          Vårdnadshavarens mejladress <span>(Obligatorisk)</span>
                        </>
                      }
                      autocomplete={"email"}
                      type="email"
                    />

                    <InputText
                      name="caregiveremailrepeat"
                      label={
                        <>
                          Upprepa mejladress <span>(Obligatorisk)</span>
                        </>
                      }
                      type="email"
                      autocomplete={"email"}
                    />

                    <InputTextNumber
                      name="caregiverphone"
                      type="tel"
                      label={
                        <>
                          Vårdnadshavarens telefonnummer<span>(frivilligt)</span>
                        </>
                      }
                      autocomplete={"tel"}
                    />
                  </>
                ) : (
                  <>
                    <Heading as="h3" className="mt-10">
                      Patientens kontaktuppgifter
                    </Heading>

                    <InputText
                      name="patientemail"
                      label={
                        <>
                          Patientens mejladress <span>(Obligatoriskt)</span>
                        </>
                      }
                      type="email"
                      autocomplete={
                        formData.compensationfor === FOR_ME ? "email" : "off"
                      }
                    />

                    <InputText
                      name="patientemailrepeat"
                      label={
                        <>
                          Upprepa mejladress <span>(Obligatoriskt)</span>
                        </>
                      }
                      type="email"
                      autocomplete={
                        formData.compensationfor === FOR_ME ? "email" : "off"
                      }
                    />

                    <InputTextNumber
                      name="patientphone"
                      type="tel"
                      label={
                        <>
                          Patientens telefonnummer <span>(frivilligt)</span>
                        </>
                      }
                      autocomplete={"tel"}
                    />
                  </>
                )}

                <p className="input-info-text -mt-2">
                  Om du saknar mejladress, då skickar du brev till oss.
                  Information om adress och vad brev ska innehålla finns på
                  sida:{" "}
                  <Link href={URLS.sickTravelToRegion}>
                    Sjukresor till annan region.
                  </Link>
                </p>
              </Box>

              <div className="flex justify-center">
                <div className="w-full text-left flex items-center">
                  <Button
                    iconPosition="left"
                    iconSize={16}
                    iconName="arrow-left"
                    aria-label="Tillbaka"
                    showIcon
                    size="medium"
                    variant="outline"
                    onClick={(event) => {
                      event.preventDefault();
                      const backUrl = undefined;
                      if (backUrl) {
                        navigate(backUrl, { replace: true });
                      } else {
                        navigate(-1);
                      }
                    }}
                  >
                    Tillbaka
                  </Button>
                </div>
                <div className="w-full text-right">
                  <Button
                    onClick={handleSubmitClick(formRef)}
                    className="btn secondary"
                    type="submit"
                  >
                    Gå vidare
                  </Button>
                </div>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
}
